import { useEffect, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { brandingApi, type BrandingSettings, type UpdateBrandingSettings } from './api'

const QUERY_KEY = ['branding-settings']
//: Static for the life of the app (it mirrors the deployed version's own
//: constants module), so it is fetched once and never treated as stale.
const OPTIONS_QUERY_KEY = ['branding-options']

export function useBrandingSettings() {
  return useQuery({ queryKey: QUERY_KEY, queryFn: brandingApi.current })
}

export function useBrandingOptions() {
  return useQuery({
    queryKey: OPTIONS_QUERY_KEY,
    queryFn: brandingApi.options,
    staleTime: Infinity,
  })
}

function useBrandingMutation<Args extends unknown[]>(
  mutate: (...args: Args) => Promise<BrandingSettings>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: Args) => mutate(...args),
    onSuccess: (data) => queryClient.setQueryData(QUERY_KEY, data),
  })
}

export function useUpdateBrandingSettings() {
  return useBrandingMutation((payload: UpdateBrandingSettings) => brandingApi.update(payload))
}

export function useUploadLogo() {
  return useBrandingMutation((file: File) => brandingApi.uploadLogo(file))
}

export function useRemoveLogo() {
  return useBrandingMutation(() => brandingApi.removeLogo())
}

export function useUploadFavicon() {
  return useBrandingMutation((file: File) => brandingApi.uploadFavicon(file))
}

export function useRemoveFavicon() {
  return useBrandingMutation(() => brandingApi.removeFavicon())
}

/**
 * An object URL for the tenant's logo or favicon, revoked on cleanup so
 * previews do not leak memory across uploads.
 *
 * Re-fetched whenever `hasAsset` flips *or* `version` changes — `hasAsset`
 * alone is not enough: replacing an existing logo leaves it `true` on both
 * sides of the upload, so the effect would never re-run and the preview
 * would keep showing the old image until a full remount. Callers pass the
 * settings query's own `dataUpdatedAt`, which changes every time the cache
 * is written — including from a successful upload's `onSuccess`.
 */
export function useBrandingAssetPreview(
  fetchUrl: () => Promise<string | null>,
  hasAsset: boolean,
  version: number,
): string | null {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!hasAsset) {
      setUrl(null)
      return
    }
    let cancelled = false
    let created: string | null = null
    void fetchUrl().then((objectUrl) => {
      if (cancelled) {
        if (objectUrl) URL.revokeObjectURL(objectUrl)
        return
      }
      created = objectUrl
      setUrl(objectUrl)
    })
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAsset, version])

  return url
}
